/**
 * Dashboards — slim orchestrator (Phase 5 refactor).
 *
 * Owns all state + side-effects; delegates rendering to three tab components
 * via a `ctx` prop bag (same pattern as ConnectorWizard / Data.jsx).
 *
 *   - DashboardDetailView         (inline opened dashboard)
 *   - MyDashboardsTab             (list / tiles of user dashboards)
 *   - PrebuiltTemplatesTab        (list / tiles of curated templates)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Plus, Search, X, Zap, Database, CheckCircle2, FlaskConical, GitBranch, Lock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useEntitlements } from "@/lib/use-entitlements";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm-context";
import { useScopeFilter } from "@/lib/scope-filter-context";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import AddSourceDialog from "@/components/dashboards/AddSourceDialog";
import SchemaMatchErrorDialog from "@/components/dashboards/SchemaMatchErrorDialog";
import DashboardDetailView from "@/components/dashboards/tabs/DashboardDetailView";
import MyDashboardsTab from "@/components/dashboards/tabs/MyDashboardsTab";
import PrebuiltTemplatesTab from "@/components/dashboards/tabs/PrebuiltTemplatesTab";
import { moduleIcon } from "@/lib/moduleIcons";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
import { useBreadcrumbLabel } from "@/lib/breadcrumb-labels";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// iter — Module icons come from the shared registry so the chips /
// banners here always reflect the icon shown in the left-hand nav.
const SenseAiIcon    = moduleIcon("sense_ai");
const DashboardsIcon = moduleIcon("dashboards");
const DataIcon       = moduleIcon("data");


/* -------------------------------------------------------------------------- */
/*  InfoBanner — compact, dismissible legend strip used at the top of each    */
/*  tab. Replaces the legacy verbose paragraph banners with chip-style legend */
/*  rows so the eye lands on actionable affordances faster. Dismissal is      */
/*  persisted per-user-per-tab via localStorage (same pattern as Section).    */
/* -------------------------------------------------------------------------- */
const CHIP_TONES = {
    primary: "bg-primary/10 text-primary border-primary/20",
    success: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    rose: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
    muted: "bg-secondary/60 text-muted-foreground border-border",
};
function InfoBanner({ tone = "primary", icon, label, summary, chips = [], trailing, storageKey, testId }) {
    const lsKey = storageKey ? `analyzio.dashboards.banner.${storageKey}.dismissed` : null;
    const [dismissed, setDismissed] = useState(() => {
        if (!lsKey) return false;
        try { return localStorage.getItem(lsKey) === "1"; } catch { return false; }
    });
    if (dismissed) return null;
    const wrapTone = tone === "primary"
        ? "border-primary/20 bg-gradient-to-br from-primary/[0.06] via-primary/[0.03] to-transparent"
        : "border-border bg-gradient-to-br from-secondary/40 via-secondary/20 to-transparent";
    return (
        <div className={`relative mb-5 rounded-xl border ${wrapTone} px-4 py-3.5 shadow-sm`} data-testid={testId}>
            <div className="flex items-start gap-3 pr-7">
                <div className="h-7 w-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    {icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-foreground">{label}</span>
                        <span className="text-xs text-muted-foreground">{summary}</span>
                    </div>
                    {(chips.length > 0 || trailing) && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                            {chips.map((c, i) => (
                                <span key={i}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide ${CHIP_TONES[c.tone] || CHIP_TONES.muted}`}>
                                    {c.icon}
                                    {c.text}
                                </span>
                            ))}
                            {trailing && (
                                <span className="ml-1 text-[11px] text-muted-foreground">{trailing}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {lsKey && (
                <button
                    type="button"
                    onClick={() => { try { localStorage.setItem(lsKey, "1"); } catch { } setDismissed(true); }}
                    className="absolute top-2.5 right-2.5 h-6 w-6 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-secondary/60 flex items-center justify-center transition-colors"
                    title="Dismiss"
                    data-testid={`${testId}-dismiss`}>
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}


export default function Dashboards() {
    const { user } = useAuth();
    const { getAccessLevel } = useSettings();
    const canWriteDashboard = ["A", "W"].includes(getAccessLevel("dashboards"));
    const isSuperAdmin = user?.role === "super_admin";
    // Plan → RBAC → limit → UI. New Dashboard locks when custom dashboards
    // aren't entitled OR the org dashboard limit is reached. SA never locked.
    const { entitled: planEntitled, limitReached: planLimitReached, openUpgrade } = useEntitlements();
    const dashCustomOff = !isSuperAdmin && !planEntitled("custom_dashboards");
    const dashLimitHit = !isSuperAdmin && planLimitReached("max_dashboards");
    const dashCreateLocked = dashCustomOff || dashLimitHit;
    const dashLockReason = dashCustomOff
        ? "Custom dashboards aren't included in your current plan. Ask your admin to upgrade to create dashboards."
        : "You've reached the number of dashboards included in your plan. Delete one or upgrade to create more.";
    // Read-only roles must not see Connect-data / Add-source affordances.
    const canWriteData = ["super_admin", "org_admin", "entity_admin"].includes(user?.role);
    const [templates, setTemplates] = useState([]);
    const [dashboards, setDashboards] = useState([]);
    const [tab, setTab] = useState(() => {
        // FIX(iter-121.187): ISSUE-1 trial onboarding — allow deep-linking to
        // the Prebuilt tab (?tab=templates) so the onboarding "View your
        // dashboard" step can send trial users straight to the now-visible
        // prebuilt templates to build a dashboard from their loaded data.
        try {
            return new URLSearchParams(window.location.search).get("tab") === "templates"
                ? "templates" : "my";
        } catch { return "my"; }
    });
    const [viewMode, setViewMode] = useViewMode("dashboards");
    const [open, setOpen] = useState(false);
    const [opened, setOpened] = useState(null); // dashboard viewed inline
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(""); // search filter — applies to both tabs
    const [category, setCategory] = useState("all"); // category filter — applies to both tabs
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [addSourceOpen, setAddSourceOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [schemaMismatch, setSchemaMismatch] = useState(null);
    const [tileSourceTarget, setTileSourceTarget] = useState(null); // {id, name}
    const [pushdownOpen, setPushdownOpen] = useState(false);
    const nav = useNavigate();
    const location = useLocation();
    // Build a /data wiring URL that remembers where we came from so the
    // Cancel button on the wiring banner returns the user to exactly that page.
    const wiringHref = (params, fromOverride) => {
        const sp = new URLSearchParams({ action: "connect", ...params });
        sp.set("from", fromOverride || (location.pathname + location.search));
        return `/data?${sp.toString()}`;
    };
    const { id } = useParams();
    const confirm = useConfirm();
    // iter — Register the opened dashboard's friendly name for the
    // breadcrumb bar so /dashboards/{id} reads as "… › Dashboards ›
    // <Dashboard title>" instead of a UUID.
    useBreadcrumbLabel(id, opened?.name || opened?.title);
    // iter-119ba — SA top-bar scope filter (Tenant → Org) drives the
    // /api/dashboards listing for SA. Non-SA users always pass null/null
    // and the backend ignores the params (RBAC-enforced server-side).
    const { tenantId: scopeTenantId, orgId: scopeOrgId } = useScopeFilter();

    const load = useCallback(async () => {
        const scopeParams = {};
        if (scopeTenantId) scopeParams.tenant_id = scopeTenantId;
        if (scopeOrgId) scopeParams.org_id = scopeOrgId;
        const [t, d] = await Promise.all([
            api.get("/dashboards/templates"),
            api.get("/dashboards", { params: scopeParams }),
        ]);
        const merged = (t.data || []).map((s) => {
            const instance = (d.data || []).find(
                (dash) => dash.source_gallery_template_id === s.id || dash.template_slug === s.id
            );
            return {
                ...s,
                instance: instance ? { id: instance.id } : null,
                _model_bound: Array.isArray(s.bind_to_models) && s.bind_to_models.length > 0,
            };
        });
        setTemplates(merged);
        setDashboards(d.data);
        if (id) {
            const all = [...merged, ...d.data];
            const found = all.find((x) => x.id === id);
            // iter-113: hydrate from detail endpoint so data_scope_warning lands
            if (found) {
                if (found.is_template) {
                    // Templates always open in the dedicated preview (KPI strip + sample data).
                    nav(`/dashboards/preview/${found.id}`, { replace: true });
                    return;
                } else {
                    try {
                        const r = await api.get(`/dashboards/${found.id}`);
                        setOpened(r.data);
                    } catch {
                        setOpened(found);
                    }
                }
            } else {
                // iter-120 round-4 · The dashboard isn't in the
                // scope-filtered list (e.g. SA pinned a scope to a
                // different tenant, OR the user landed on a deep-link
                // outside their current filter). Always honour the URL
                // by fetching the dashboard directly — RBAC at the
                // endpoint enforces access. If the fetch fails (403/
                // 404), we surface that via the existing catch path.
                try {
                    const r = await api.get(`/dashboards/${id}`);
                    setOpened(r.data);
                } catch {
                    setOpened(null);
                }
            }
        }
        setLoading(false);
    }, [scopeTenantId, scopeOrgId, id]);
    // iter-120 · clear the inline detail view when the user navigates
    // back to `/dashboards` (no `:id` in URL) — e.g. by clicking the
    // sidebar "Dashboards" link while already on `/dashboards/<id>`.
    // Without this, `opened` stayed set so the page continued to show
    // the detail view even though the URL had returned to the list.
    useEffect(() => {
        if (!id) setOpened(null);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    // iter-119u (C2): refetch when SA toggles template enablement so OA
    // sees newly-enabled prebuilts without a hard refresh.
    // iter-119ay: ALSO refetch on tab visibility change so cross-browser /
    // cross-device scenarios (SA on laptop, OA on phone) pick up the new
    // enablement when the user comes back to the tab. The intra-tab
    // `gallery:templates-changed` event only fires within the same
    // browser session.
    useEffect(() => {
        const onChanged = () => load();
        const onVisible = () => { if (document.visibilityState === "visible") load(); };
        window.addEventListener("gallery:templates-changed", onChanged);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("gallery:templates-changed", onChanged);
            document.removeEventListener("visibilitychange", onVisible);
        };
  }, [load]);

    // iter-113: list payload omits per-dashboard fields. Always re-fetch
    // detail before opening so the inline view has everything.
    // iter-120 · navigate to `/dashboards/<id>` so the URL reflects the
    // open detail view. Required for the sidebar "Dashboards" link to
    // pop back to the list — without a URL transition the click target
    // would resolve to the same path the user is already on (`useParams
    // .id` would stay undefined) and the `opened` state would be sticky.
    const openDashboard = async (d) => {
        if (!d?.id) return;
        try {
            const r = await api.get(`/dashboards/${d.id}`);
            setOpened(r.data);
        } catch (e) {
            setOpened(d);
            toast.error(e?.response?.data?.detail || "Could not load dashboard details");
        }
        nav(`/dashboards/${d.id}`);
    };

    const clone = async (tpl) => {
        // Every template is a Sense AI Builder prebuilt. Clone via the
        // idempotent instantiator — a second call returns the same dashboard.
        const params = {};
        if (scopeTenantId) params.tenant_id = scopeTenantId;
        if (scopeOrgId) params.org_id = scopeOrgId;
        const r = await api.post(`/dashboards/from-template/${tpl.id}`, null, { params });
        setDashboards((prev) => {
            const filtered = prev.filter((d) => d.id !== r.data.id);
            return [r.data, ...filtered];
        });
        toast.success(tpl.instance ? `Opened "${tpl.name}"` : `Cloned "${tpl.name}"`);
        setOpened(r.data);
        nav(`/dashboards/${r.data.id}`);
        return r.data;
    };

    // Delete a template-instantiated dashboard, leaving the template intact.
    const delTemplateInstance = async (tpl) => {
        const instId = tpl.instance?.id;
        if (!instId) return;
        const ok = await confirm({
            title: `Delete instantiated "${tpl.name}"?`,
            description: "Removes the dashboard instance from My Dashboards. The template stays available — you can re-instantiate it at any time.",
            confirmLabel: "Delete instance",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/dashboards/${instId}`);
            toast.success("Instance deleted");
            setDashboards((prev) => prev.filter((d) => d.id !== instId));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const createBlank = async () => {
        if (!name.trim()) return;
        const r = await api.post("/dashboards", { name, widgets: [] });
        toast.success("Dashboard created");
        setDashboards([r.data, ...dashboards]);
        setOpen(false); setName("");
        setOpened(r.data);
        nav(`/dashboards/${r.data.id}`);
    };

    const del = async (id, name) => {
        const ok = await confirm({
            title: `Delete dashboard "${name}"?`,
            description: "Removes the dashboard. If it was created from a template, the template stays available — you can re-instantiate any time.",
            confirmLabel: "Delete dashboard", destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/dashboards/${id}`);
            toast.success("Dashboard deleted");
            setDashboards((prev) => prev.filter((d) => d.id !== id));
            // Refresh templates so template-instance buttons (Open ↔ Instantiate) update.
            try {
                const tpl = await api.get("/templates");
                setTemplates((prev) => {
                    const legacy = prev.filter((p) => !p._model_bound);
                    return [
                        ...(tpl.data || []).map((m) => ({ ...m, _model_bound: true, widgets: m.widgets || [] })),
                        ...legacy,
                    ];
                });
            } catch { /* non-blocking */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // iter-67: Sense AI Builder prebuilts ARE Gallery items
    // (kind=prebuilt_dashboard). Route through DELETE /gallery/{id}
    // so super_admin/owner enforcement lives in gallery_routes.
    const savePlaybookFromTemplate = async (tpl) => {
        if (!isSuperAdmin) return;
        try {
            const sourceId = tpl?.instance?.id;
            if (sourceId) {
                await api.post(`/sense-ai-builder/playbooks/from-dashboard/${sourceId}`, {
                    name: tpl.name,
                });
            } else {
                await api.post(`/sense-ai-builder/playbooks/from-template/${tpl.id}`, {});
            }
            toast.success(`Saved "${tpl.name}" as playbook`, {
                description: "Future Builder prompts matching its keywords will use it as RAG context. Edit keywords in /playbooks.",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save as playbook");
        }
    };

    const hideTemplate = async (tpl) => {
        if (!isSuperAdmin) return;
        const ok = await confirm({
            title: "Delete template?",
            description: (
                <>Permanently delete the prebuilt template <span className="font-medium">{tpl.name}</span>?
                    Existing instantiated dashboards keep working — only the template definition is removed.
                </>
            ),
            confirmLabel: "Delete template",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/gallery/${tpl.id}`);
            toast.success(`${tpl.name} removed from prebuilts`);
            setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
            // Broadcast so Platform Templates / Visual Gallery refetch.
            window.dispatchEvent(new CustomEvent("gallery:templates-changed", {
                detail: { id: tpl.id, action: "deleted" },
            }));
            // Backend cascades dashboard instances bound to this template;
            // reload so My Dashboards reflects the cascade.
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    // Derived: filtered lists honoring search + category across BOTH tabs
    const matches = useCallback((d) => {
        const qMatch = !q || (d.name || "").toLowerCase().includes(q.toLowerCase()) ||
            (d.description || "").toLowerCase().includes(q.toLowerCase());
        const cMatch = category === "all" || (d.category || "general") === category;
        return qMatch && cMatch;
    }, [q, category]);
    // iter-121.131 — Pagination + descending order.
    //
    // (1) Sort by `created_at` DESC so newly-added dashboards land at
    //     the TOP of "My dashboards", matching the user's "added
    //     dashboard should appear first" expectation. Falls back to
    //     `updated_at` then `id` for back-compat with older rows that
    //     might lack a `created_at`. We sort the FULL list (not just
    //     the filtered slice) so the order is stable across searches.
    //
    // (2) Pagination — 12 items per page (3 cols × 4 rows in the tile
    //     grid; matches the page's natural density). State is held in
    //     the URL via `?my_page=N&prebuilt_page=N` so deep-links and
    //     reloads survive. Page resets to 1 whenever the search query
    //     or category filter changes (otherwise paging beyond the end
    //     of the filtered list would render empty).
    const _sortDesc = (a, b) => {
        const ta = +new Date(a.created_at || a.updated_at || 0);
        const tb = +new Date(b.created_at || b.updated_at || 0);
        if (ta !== tb) return tb - ta;
        return (b.id || "").localeCompare(a.id || "");
    };
    const sortedDashboards = useMemo(() => [...dashboards].sort(_sortDesc), [dashboards]);
    const sortedTemplates = useMemo(() => [...templates].sort(_sortDesc), [templates]);
    const filteredDashboards = useMemo(() => sortedDashboards.filter(matches), [sortedDashboards, matches]);
    const filteredTemplates  = useMemo(() => sortedTemplates.filter(matches),  [sortedTemplates, matches]);

    const [searchParams, setSearchParams] = useSearchParams();
    const myPage = Math.max(1, parseInt(searchParams.get("my_page") || "1", 10) || 1);
    const prebuiltPage = Math.max(1, parseInt(searchParams.get("prebuilt_page") || "1", 10) || 1);
    const myPageCount = Math.max(1, Math.ceil(filteredDashboards.length / PAGE_SIZE));
    const prebuiltPageCount = Math.max(1, Math.ceil(filteredTemplates.length / PAGE_SIZE));
    // Defensive clamp — if filters shrink the list past the current
    // page, slide back to the new last page without scrolling the URL.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        let changed = false;
        if (myPage > myPageCount) { next.set("my_page", String(myPageCount)); changed = true; }
        if (prebuiltPage > prebuiltPageCount) { next.set("prebuilt_page", String(prebuiltPageCount)); changed = true; }
        // Drop the param entirely when it's "1" to keep the URL tidy.
        if (next.get("my_page") === "1") { next.delete("my_page"); changed = changed || searchParams.has("my_page"); }
        if (next.get("prebuilt_page") === "1") { next.delete("prebuilt_page"); changed = changed || searchParams.has("prebuilt_page"); }
        if (changed) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myPageCount, prebuiltPageCount]);
    // When the user types in search or picks a category, slide back to
    // page 1 so they don't stare at an empty page.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        let changed = false;
        if (next.has("my_page")) { next.delete("my_page"); changed = true; }
        if (next.has("prebuilt_page")) { next.delete("prebuilt_page"); changed = true; }
        if (changed) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, category]);
    const pagedDashboards = useMemo(
        () => filteredDashboards.slice((myPage - 1) * PAGE_SIZE, myPage * PAGE_SIZE),
        [filteredDashboards, myPage],
    );
    const pagedTemplates = useMemo(
        () => filteredTemplates.slice((prebuiltPage - 1) * PAGE_SIZE, prebuiltPage * PAGE_SIZE),
        [filteredTemplates, prebuiltPage],
    );
    const setMyPage = (n) => {
        const next = new URLSearchParams(searchParams);
        if (n <= 1) next.delete("my_page");
        else next.set("my_page", String(n));
        setSearchParams(next, { replace: true });
    };
    const setPrebuiltPage = (n) => {
        const next = new URLSearchParams(searchParams);
        if (n <= 1) next.delete("prebuilt_page");
        else next.set("prebuilt_page", String(n));
        setSearchParams(next, { replace: true });
    };
    const categories = ["all", ...Array.from(new Set(
        [...dashboards, ...templates].map((x) => x.category || "general")
    )).sort()];

    if (opened) {
        return (
            <DashboardDetailView ctx={{
                opened, setOpened, setDashboards, nav,
                canWriteData, canWriteDashboard, isSuperAdmin,
                wiringHref, clone, load,
                sourcesOpen, setSourcesOpen,
                addSourceOpen, setAddSourceOpen,
                shareOpen, setShareOpen,
                galleryOpen, setGalleryOpen,
                pushdownOpen, setPushdownOpen,
                schemaMismatch, setSchemaMismatch,
            }} />
        );
    }

    const prebuiltLocked = !isSuperAdmin && !planEntitled("prebuilt_dashboards");
    const customLocked = !isSuperAdmin && !planEntitled("custom_dashboards");
    const myTabCtx = {
        viewMode,
        // iter-121.131 — pass the PAGED slice (12 rows max) instead of
        // the full filtered list. Tab components are pagination-naive;
        // they just render whatever they receive.
        filteredDashboards: pagedDashboards,
        dashboards, loading, canWriteData, canWriteDashboard,
        openDashboard, setTileSourceTarget, del,
        customLocked, openUpgrade,
    };
    const prebuiltTabCtx = {
        viewMode,
        filteredTemplates: pagedTemplates,
        isSuperAdmin, setOpened, nav, clone,
        delTemplateInstance, hideTemplate, savePlaybookFromTemplate,
        setTileSourceTarget, load,
        prebuiltLocked, openUpgrade,
    };

    return (
        <div className="fadein">
            <PageHeader
                eyebrow="Visualize"
                title="Dashboards"
                description="Explore prebuilt dashboards or create your own."
                testId="dashboards-header"
                actions={
                    canWriteDashboard && (
                        dashCreateLocked ? (
                            <Button variant="outline" className="rounded-md opacity-90"
                                title={dashLockReason}
                                onClick={() => openUpgrade()}
                                data-testid="dashboard-create-button-locked">
                                <Lock className="h-4 w-4 sm:mr-1.5" />
                                <span className="hidden sm:inline">New dashboard</span>
                                <Badge variant="secondary" className="ml-2 text-[9px] font-mono uppercase">Upgrade</Badge>
                            </Button>
                        ) : (
                            <Button onClick={() => setOpen(true)} className="rounded-md shadow-sm" data-testid="dashboard-create-button" title="New dashboard">
                                <Plus className="h-4 w-4 sm:mr-1.5" />
                                <span className="hidden sm:inline">New dashboard</span>
                            </Button>
                        )
                    )
                }
            />
            <PageContent>
                {/* Global filter bar — applies to BOTH My dashboards and Templates */}
                <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="dashboards-filter-bar">
                    <div className="relative flex-1 min-w-[260px] max-w-md">
                        <Search className="h-4 w-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search dashboards and templates..."
                            className="w-full h-10 pl-10 pr-3 bg-card border border-border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                            data-testid="dashboards-search" />
                    </div>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="h-10 w-48 rounded-lg text-sm bg-card shadow-sm border-border" data-testid="dashboards-category-filter">
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => (
                                <SelectItem key={c} value={c} data-testid={`dashboards-category-opt-${c}`}>
                                    {c === "all" ? "All categories" : c}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {(q || category !== "all") && (
                        <Button variant="ghost" size="sm" className="h-10 rounded-lg gap-1"
                            onClick={() => { setQ(""); setCategory("all"); }}
                            data-testid="dashboards-filter-clear">
                            <X className="h-3.5 w-3.5" /> Clear
                        </Button>
                    )}
                    <div className="ml-auto">
                        <ViewToggle value={viewMode} onChange={setViewMode} testIdPrefix="dashboards-view" />
                    </div>
                </div>
                <Tabs value={tab} onValueChange={setTab}>
                    <div className="mb-6 border-b border-border">
                        <TabsList className="bg-transparent rounded-none justify-start h-auto p-0 gap-1">
                            <TabsTrigger value="my" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary px-4 py-3 -mb-px text-sm font-medium transition-colors" data-testid="dashboards-tab-my">My dashboards ({filteredDashboards.length})</TabsTrigger>
                            <TabsTrigger value="templates" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary px-4 py-3 -mb-px text-sm font-medium transition-colors" data-testid="dashboards-tab-templates">Prebuilt ({filteredTemplates.length})</TabsTrigger>
                        </TabsList>
                    </div>
                    <TabsContent value="my">
                        <InfoBanner
                            tone="muted"
                            storageKey="my-dashboards-banner"
                            testId="my-dashboards-info"
                            icon={<GitBranch className="h-4 w-4 text-primary" />}
                            label="My dashboards"
                            summary="Your live working set — instantiated prebuilts, AI-generated drafts, and schema-matched auto-clones."
                            chips={[
                                { tone: "amber", icon: <DataIcon className="h-3 w-3" />, text: "Connect data" },
                                { tone: "primary", icon: <Plus className="h-3 w-3" />, text: "Add source" },
                                { tone: "primary", icon: <DashboardsIcon className="h-3 w-3" />, text: "Prebuilt" },
                                { tone: "amber", icon: <SenseAiIcon className="h-3 w-3" />, text: "Sense AI" },
                                { tone: "primary", icon: <GitBranch className="h-3 w-3" />, text: "Unified" },
                            ]}
                        />
                        <MyDashboardsTab ctx={myTabCtx} />
                        {/* iter-121.131 — Footer pager. Shown only when
                            the filtered list overflows a single page so
                            single-page tenants don't see a redundant
                            "Page 1 of 1" control. */}
                        {filteredDashboards.length > PAGE_SIZE && (
                            <PagerFooter
                                page={myPage}
                                pageCount={myPageCount}
                                pageSize={PAGE_SIZE}
                                total={filteredDashboards.length}
                                onChange={setMyPage}
                                testIdPrefix="my-dashboards"
                            />
                        )}
                    </TabsContent>
                    <TabsContent value="templates">
                        <InfoBanner
                            tone="primary"
                            storageKey="prebuilts-banner"
                            testId="templates-info"
                            icon={<DashboardsIcon className="h-4 w-4 text-primary" />}
                            label="Prebuilts"
                            summary="Instant dashboards with sample data to help you get started."
                            chips={[
                                { tone: "rose", icon: <DataIcon className="h-3 w-3" />, text: "Connect data" },
                                { tone: "success", icon: <CheckCircle2 className="h-3 w-3" />, text: "Live ready" },
                                { tone: "primary", icon: <Plus className="h-3 w-3" />, text: "Add source" },
                                { tone: "muted", icon: <GitBranch className="h-3 w-3" />, text: "Bound to canonical model" },
                                { tone: "amber", icon: <FlaskConical className="h-3 w-3" />, text: "Demo mode" },
                            ]}
                            trailing="Sample data until you connect a source."
                        />
                        <PrebuiltTemplatesTab ctx={prebuiltTabCtx} />
                        {filteredTemplates.length > PAGE_SIZE && (
                            <PagerFooter
                                page={prebuiltPage}
                                pageCount={prebuiltPageCount}
                                pageSize={PAGE_SIZE}
                                total={filteredTemplates.length}
                                onChange={setPrebuiltPage}
                                testIdPrefix="prebuilts"
                            />
                        )}
                    </TabsContent>
                </Tabs>
            </PageContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New dashboard</DialogTitle>
                        <DialogDescription>Give your dashboard a name. You can add widgets after it's created.</DialogDescription>
                    </DialogHeader>
                    <div>
                        <label className="eyebrow mb-1.5 block">Name</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 bg-card border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all" data-testid="dashboard-name-input" />
                    </div>
                    <DialogFooter>
                        <Button onClick={createBlank} className="rounded-md" data-testid="dashboard-create-submit">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Shared AddSourceDialog driven by tile/template clicks across
                My Dashboards + Prebuilts — single source of truth for
                "Connect data" / "Add source" UX. */}
            <AddSourceDialog
                open={!!tileSourceTarget}
                onClose={() => setTileSourceTarget(null)}
                dashboardId={tileSourceTarget?.id}
                dashboardName={tileSourceTarget?.name}
                onAttached={() => { setTileSourceTarget(null); load(); }}
                onConnectNew={() => { const t = tileSourceTarget; setTileSourceTarget(null); if (t) nav(wiringHref({ dashboard_id: t.id, append: 1 })); }}
                onSchemaMismatch={(p) => { setTileSourceTarget(null); setSchemaMismatch(p); }}
            />
            <SchemaMatchErrorDialog
                open={!!schemaMismatch}
                onClose={() => setSchemaMismatch(null)}
                payload={schemaMismatch}
            />
        </div>
    );
}


/**
 * iter-121.131 — Minimal Prev / Page X of Y / Next footer used by
 * both "My dashboards" and "Prebuilts". Built inline rather than
 * lifted into shadcn `Pagination` because we only need the three
 * affordances + a small range label, and the shadcn primitive ships
 * styled `<a>` tags that we'd have to override to play nice with
 * SPA routing anyway.
 */
function PagerFooter({ page, pageCount, pageSize, total, onChange, testIdPrefix = "pager" }) {
    if (pageCount <= 1) return null;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(total, page * pageSize);
    return (
        <div
            className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-4"
            data-testid={`${testIdPrefix}-pager`}
        >
            <div className="text-[11px] text-muted-foreground font-mono">
                <span data-testid={`${testIdPrefix}-pager-range`}>
                    {start}–{end} of {total}
                </span>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    variant="outline" size="sm"
                    className="h-7 text-xs rounded-sm"
                    onClick={() => onChange(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    data-testid={`${testIdPrefix}-pager-prev`}
                >
                    Prev
                </Button>
                <span
                    className="text-[11px] text-muted-foreground font-mono px-1"
                    data-testid={`${testIdPrefix}-pager-page`}
                >
                    Page {page} of {pageCount}
                </span>
                <Button
                    variant="outline" size="sm"
                    className="h-7 text-xs rounded-sm"
                    onClick={() => onChange(Math.min(pageCount, page + 1))}
                    disabled={page >= pageCount}
                    data-testid={`${testIdPrefix}-pager-next`}
                >
                    Next
                </Button>
            </div>
        </div>
    );
}
